type FlushCallback = () => void | Promise<void>;

const flushCallbacks = new Set<FlushCallback>();

/**
 * Register a flush for debounced saves. Logout awaits every registered flush
 * first, so pending edits reach the server under the current session.
 */
export function registerPendingSaveFlush(callback: FlushCallback): () => void {
  flushCallbacks.add(callback);
  return () => {
    flushCallbacks.delete(callback);
  };
}

/**
 * Run every registered flush (my weeks, and a friend's in friend mode), even
 * after one fails, so one stuck queue doesn't keep the others unsaved; then
 * reject with the first failure.
 */
export async function flushPendingSaves(): Promise<void> {
  const [results] = await Promise.all([
    Promise.allSettled(Array.from(flushCallbacks, (callback) => callback())),
    // Saves of a provider that already unmounted: wait for them so a read
    // that follows (Compras reading the friend's week) sees them. Their
    // failures aren't retryable from here; the provider reported them.
    Promise.allSettled(leavingFlushes),
  ]);
  const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
  if (failed) throw failed.reason;
}

// ── Saves that outlive their provider ──

const leavingFlushes = new Set<Promise<void>>();

/**
 * A flush started as its provider unmounts (leaving friend mode): its flush
 * callback is unregistered by then, so track the save itself until it
 * settles. Returns `flush` unchanged.
 */
export function trackLeavingFlush(flush: Promise<void>): Promise<void> {
  leavingFlushes.add(flush);
  const done = () => {
    leavingFlushes.delete(flush);
  };
  flush.then(done, done);
  return flush;
}

type LostEditListener = (username: string) => void;
const lostEditListeners = new Set<LostEditListener>();

/** A friend's week left with an edit that then failed to save: tell whoever listens (the navbar's toast). */
export function reportLostFriendEdit(username: string): void {
  for (const listener of lostEditListeners) listener(username);
}

export function onLostFriendEdit(listener: LostEditListener): () => void {
  lostEditListeners.add(listener);
  return () => {
    lostEditListeners.delete(listener);
  };
}

/**
 * True from the moment logout starts flushing until it fails or the next
 * login: saves still flush, but follow-up reads (week-list refresh) and
 * unmount flushes are skipped, since the session is about to be gone.
 */
let sessionEnding = false;

export function isSessionEnding(): boolean {
  return sessionEnding;
}

export function setSessionEnding(ending: boolean): void {
  sessionEnding = ending;
}
